import React, { useState } from "react";
import UseContext from "hooks/useContext";
import SectionWrapper from "pages/createMission/components/SectionWrapper";
import Button from "components/button";
import StyledInput from "components/styledInput/StyledInput";
import Tooltip from "components/tooltips/Tooltips";
import DrawnGlyph from "components/emptyState/DrawnGlyph";
import { CrossIcon, Plus } from "assets/icons";
import ApiDataBase from "server";
import {
    ADD_TASK_ERROR,
    TASK_LABEL_MAX_LENGTH,
    TASK_SOURCE,
    addCompanyTask,
    applyTaskCheckResult,
    buildTaskCheckBody,
    cleanTaskLabel,
    countSelectedTasks,
    isTaskCheckPending,
    removeCompanyTask,
    toggleProposedTask,
} from "pages/missionTemplates/utils/templateTasks";

const TasksTemplate = () => {
    const { templateData, setTemplateData, language } = UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.tasks.${key}`, fallback);
    const [newTask, setNewTask] = useState("");
    const [addError, setAddError] = useState(null);

    const rows = templateData.tasks || [];
    const selectedCount = countSelectedTasks(rows);
    // Proposed or added: any task in the list makes the message pointless.
    const hasTasks = rows.some(
        (row) =>
            row.source === TASK_SOURCE.QUALIFICATION ||
            row.source === TASK_SOURCE.COMPANY
    );
    const canAdd = cleanTaskLabel(newTask) !== "";

    // Errors of the AI check live in templateData: the answer can come back
    // after the user left the step, and must still be shown on return.
    const shownError = addError
        ? { error: addError }
        : templateData.taskCheckError || null;

    const updateRows = (update) =>
        setTemplateData((prev) => ({ ...prev, tasks: update(prev.tasks || []) }));

    const clearTaskCheckError = () =>
        setTemplateData((prev) =>
            prev.taskCheckError ? { ...prev, taskCheckError: null } : prev
        );

    // Functional update: other tasks may have been added, removed or
    // checked meanwhile, and only this row (by key) is touched.
    const settleTaskCheck = (key, response) =>
        setTemplateData((prev) => {
            const result = applyTaskCheckResult(prev.tasks, key, response);
            return {
                ...prev,
                tasks: result.rows,
                ...(result.error
                    ? {
                          taskCheckError: {
                              error: result.error,
                              label: result.label,
                          },
                      }
                    : {}),
            };
        });

    const checkAddedTask = (key, label) => {
        ApiDataBase.postCheckMissionTemplateTask(
            buildTaskCheckBody(label, templateData.qualification),
            (response) => settleTaskCheck(key, response),
            // Network error, timeout, 4xx or 5xx: the typed label stays.
            () => settleTaskCheck(key, null)
        );
    };

    const handleAdd = () => {
        const result = addCompanyTask(rows, newTask);
        if (result.error) {
            setAddError(result.error === ADD_TASK_ERROR.EMPTY ? null : result.error);
            return;
        }
        updateRows(() => result.rows);
        setNewTask("");
        setAddError(null);
        clearTaskCheckError();
        checkAddedTask(result.key, result.label);
    };

    const handleKeyDown = (e) => {
        if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
        e.preventDefault();
        handleAdd();
    };

    // StyledInput only caps textareas: the label is capped here, as the
    // former maxLength did.
    // The AI check error is kept while typing: it often arrives while the user
    // is already typing the next task, and would vanish unread. The next add
    // clears it.
    const handleChange = (e) => {
        setNewTask(e.target.value.slice(0, TASK_LABEL_MAX_LENGTH));
        if (addError) setAddError(null);
    };

    const errorMessage = (() => {
        switch (shownError?.error) {
            case ADD_TASK_ERROR.DUPLICATE:
                return t("duplicate", "Cette tâche est déjà dans la liste.");
            case ADD_TASK_ERROR.TOO_MANY:
                return t("tooMany", "Le nombre maximum de tâches est atteint.");
            case ADD_TASK_ERROR.REJECTED:
                // Function replacement: a label containing "$&" stays as typed.
                return t(
                    "rejected",
                    "« {label} » n'a pas été ajoutée : ce texte ne décrit pas une tâche du poste."
                ).replace("{label}", () => shownError.label || "");
            default:
                return null;
        }
    })();

    const addPlaceholder = t(
        "addPlaceholder",
        "Ajouter une tâche (ex : accueil des livreurs)"
    );
    const removeLabel = t("removeLabel", "Retirer");

    return (
        <SectionWrapper
            title={t("title", "Tâches du poste")}
            description={t(
                "description",
                "Cochez les tâches proposées ou ajoutez les vôtres."
            )}
        >
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                    {/* StyledInput takes no onKeyDown: Enter is caught as it
                        bubbles up from the field. */}
                    <div className="flex-1 min-w-0" onKeyDown={handleKeyDown}>
                        <StyledInput
                            type={"text"}
                            value={newTask}
                            onChange={handleChange}
                            placeholder={addPlaceholder}
                        />
                    </div>
                    <Button
                        size={"SM"}
                        color={canAdd ? "PRIMARY" : "DISABLED"}
                        className={`h-10 shrink-0 ${
                            canAdd ? "" : "cursor-not-allowed"
                        }`}
                        onClick={handleAdd}
                        disabled={!canAdd}
                    >
                        <Plus wh={12} color={canAdd ? "#FFFFFF" : "#9CA3AF"} />
                        {t("addButton", "Ajouter")}
                    </Button>
                </div>
                {errorMessage && (
                    <p className="ft-sm text-red-500" role="alert">
                        {errorMessage}
                    </p>
                )}
            </div>
            {hasTasks ? (
                // The checkboxes show what is selected; only say why Next is blocked.
                selectedCount === 0 ? (
                    <p className="text-sm text-gray-500" aria-live="polite">
                        {t("selectAtLeastOne", "Cochez au moins une tâche pour continuer.")}
                    </p>
                ) : null
            ) : (
                // Empty state of the step, in the look of the page empty states (badge, title, text).
                <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-100 px-6 py-8 text-center">
                    <div className="relative mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                        <DrawnGlyph name="squareCheck" size={36} />
                    </div>
                    <p className="text-base font-medium text-gray-900">
                        {t("emptyTitle", "Aucune tâche pour l'instant")}
                    </p>
                    <p className="max-w-md text-sm text-gray-500">
                        {t(
                            "noProposal",
                            "Aucune tâche n'est proposée pour cette qualification. Ajoutez avec le champ ci-dessus celles que l'intérimaire devra réaliser."
                        )}
                    </p>
                </div>
            )}
            {rows.length > 0 && (
                // One bordered list, as the hardship factors above: the checkbox says what is
                // selected, no coloured border.
                <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100 bg-white">
                    {rows.map((row) => {
                        if (row.source !== TASK_SOURCE.COMPANY) {
                            return (
                                <li
                                    key={row.key}
                                    className="flex px-5 py-4 hover:bg-gray-50"
                                >
                                    <StyledInput
                                        type={"checkbox"}
                                        checked={!!row.checked}
                                        onChange={() =>
                                            updateRows((prev) =>
                                                toggleProposedTask(prev, row.key)
                                            )
                                        }
                                        label={
                                            <span className="min-w-0 break-words">
                                                {row.label}
                                            </span>
                                        }
                                    />
                                </li>
                            );
                        }
                        const isPending = isTaskCheckPending(row);
                        return (
                            <li
                                key={row.key}
                                aria-busy={isPending}
                                className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    {/* Always selected: checked, as the proposed tasks; the cross removes it. */}
                                    <input
                                        type="checkbox"
                                        checked
                                        readOnly
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        className={`checkbox-primary pointer-events-none mr-1 ${
                                            isPending ? "opacity-40" : ""
                                        }`}
                                    />
                                    <span
                                        className={
                                            isPending
                                                ? "rounded-md border border-dashed border-gray-300 px-2 py-0.5 text-sm text-gray-400 break-words"
                                                : "text-sm text-gray-900 break-words"
                                        }
                                    >
                                        {row.label}
                                    </span>
                                </div>
                                {/* The badge sits by the cross: both are about this added task. */}
                                <div className="flex shrink-0 items-center gap-2">
                                    {isPending ? (
                                        <span className="badge badge-sm border border-dashed border-gray-300 bg-white text-gray-400">
                                            {t("checking", "Vérification en cours")}
                                        </span>
                                    ) : (
                                        <span className="badge badge-sm bg-gray-100 text-gray-600">
                                            {t("addedByYou", "Ajoutée par vous")}
                                        </span>
                                    )}
                                    <Tooltip text={removeLabel} placement="top">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                updateRows((prev) =>
                                                    removeCompanyTask(prev, row.key)
                                                )
                                            }
                                            aria-label={`${removeLabel} ${row.label}`}
                                            className="shrink-0 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                        >
                                            <CrossIcon wh={20} color="#111827" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </SectionWrapper>
    );
};

export default TasksTemplate;
